import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import { zAssistantChat, listZProjects, listZCreations, Z_ASSISTANT_ACTION_MAP, type ZAssistantTurn } from '@/lib/zstudio';

const STARTERS = [
  'Je veux faire une publicité pour ma nouvelle robe',
  'Comment créer une vidéo de défilé ?',
  'Propose-moi un lookbook pour ma collection',
  'Quels contenus pour Ramadan ?',
];

export default function ZStudioAssistantPage() {
  const { user } = useAuth();
  const [turns, setTurns] = useState<ZAssistantTurn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [context, setContext] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([listZProjects(user.id), listZCreations(user.id)]).then(([projects, creations]) => {
      const lines = [
        `Projets récents : ${projects.slice(0, 5).map((p) => p.name).join(', ') || 'aucun'}.`,
        `Créations : ${creations.length} (${creations.filter((c) => c.media_kind === 'image').length} images, ${creations.filter((c) => c.media_kind === 'video').length} vidéos).`,
      ];
      setContext(lines.join(' '));
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns, loading]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || loading) return;
    const next: ZAssistantTurn[] = [...turns, { role: 'user', content }];
    setTurns(next);
    setInput('');
    setLoading(true);
    setError(null);
    setSuggestions([]);
    setActions([]);
    try {
      const res = await zAssistantChat({ messages: next.slice(-8), context, language: 'fr' });
      setTurns((prev) => [...prev, { role: 'assistant', content: res.reply }]);
      setSuggestions(res.suggestions);
      setActions(res.actions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'L’assistant n’a pas pu répondre.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-robot-2-line text-accent-500"></i>ZStudio AI
        </h2>
        <p className="text-sm text-foreground-500 mt-1">L’assistant créatif qui connaît votre studio. Il conseille et propose des actions — il ne publie jamais à votre place (spec 51 & 53).</p>
      </div>
      <ZStudioNav />

      <div className="bg-background-50 border border-background-200/70 rounded-xl flex flex-col h-[62vh] min-h-[420px]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {turns.length === 0 && (
            <div className="text-center py-8">
              <span className="w-14 h-14 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mx-auto mb-3">
                <i className="ri-robot-2-line text-2xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-800">Bonjour ! Que souhaitez-vous créer aujourd’hui ?</p>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4 max-w-xl mx-auto">
                {STARTERS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer">{s}</button>
                ))}
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words ${t.role === 'user' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-800'}`}>
                {t.content}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="px-3.5 py-2.5 rounded-2xl bg-background-100"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div></div>}
          <div ref={endRef} />
        </div>

        {error && <div className="mx-4 mb-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

        {(suggestions.length > 0 || actions.length > 0) && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {actions.map((a) => {
              const m = Z_ASSISTANT_ACTION_MAP[a];
              if (!m) return null;
              return (
                <Link key={a} to={m.path} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-magic-line"></i>{m.label}
                </Link>
              );
            })}
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => send(s)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap">
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="p-3 border-t border-background-200/70 flex items-center gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Écrivez votre demande…"
            className="flex-1 px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
          />
          <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 rounded-full bg-accent-500 text-background-50 flex items-center justify-center hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-send-plane-2-line"></i>
          </button>
        </form>
      </div>
    </div>
  );
}